import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import agthealthService from '../../../../services/agthealthService';
import { useTasksContext } from '../../../../contexts/TasksContext';

// Iconos
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventIcon from '@mui/icons-material/Event';

// Color dorado
const goldColor = '#E3AE08';

// Estilos
const TasksPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: 'none',
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
}));

const TasksHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const TasksContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  overflow: 'auto',
  maxHeight: '400px',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#E3AE08',
    borderRadius: '3px',
  }
}));

const PriorityChip = styled(Chip)(({ priority }) => {
  let color;
  switch (priority) {
    case 'high':
      color = '#d32f2f';
      break;
    case 'medium':
      color = '#ed6c02';
      break;
    case 'low':
    default:
      color = '#2e7d32';
      break;
  }
  
  return {
    backgroundColor: `${color}20`,
    color: color,
    fontWeight: 500,
    fontSize: '0.7rem',
    height: '20px',
  };
});

const DueDateText = styled(Typography)(({ theme, isPast }) => ({
  fontSize: '0.7rem',
  color: isPast ? '#d32f2f' : '#757575',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
  color: goldColor,
  fontWeight: 600,
  fontSize: '0.8rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(227, 174, 8, 0.08)',
  },
}));

const EmptyTasksBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  height: '200px',
  color: '#757575',
}));

const TasksWidget = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Acceder al contexto de tareas para saber cuándo refrescar
  const { refreshTrigger } = useTasksContext();
  
  // Cargar tareas
  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await agthealthService.getTasksForWidget(5);
      if (response.success) {
        setTasks(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar tareas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Cargar tareas al inicio y cuando cambie refreshTrigger
  useEffect(() => {
    loadTasks();
    
    // También configurar listener para el evento de creación de tareas como respaldo
    const handleTaskCreated = () => {
      console.log('Evento de tarea creada detectado, actualizando widget de tareas');
      loadTasks();
    };
    
    window.addEventListener('agtTaskCreated', handleTaskCreated);
    
    return () => {
      window.removeEventListener('agtTaskCreated', handleTaskCreated);
    };
  }, [refreshTrigger]);
  
  // Manejar cambio en estado de completado
  const handleToggleComplete = async (taskId, completed) => {
    try {
      await agthealthService.toggleTaskCompletion(taskId, !completed);
      
      // Actualizar localmente para respuesta inmediata
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.taskId === taskId ? { ...task, completed: !completed } : task
        )
      );
      
      // Recargar después para sincronizar con el servidor
      setTimeout(() => {
        loadTasks();
      }, 300);
    } catch (error) {
      console.error('Error al cambiar estado de tarea:', error);
    }
  };
  
  // Formatear fecha
  const formatDueDate = (dateString) => {
    if (!dateString) return '';
    
    const dueDate = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const isPast = dueDate < today;
    const isToday = dueDate.toDateString() === today.toDateString();
    const isTomorrow = dueDate.toDateString() === tomorrow.toDateString();
    
    let formattedDate;
    if (isToday) {
      formattedDate = 'Hoy';
    } else if (isTomorrow) {
      formattedDate = 'Mañana';
    } else {
      formattedDate = dueDate.toLocaleDateString(undefined, { 
        day: 'numeric', 
        month: 'short' 
      });
    }
    
    return { formattedDate, isPast };
  };
  
  // Traducir prioridad
  const translatePriority = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return 'Normal';
    }
  };

  return (
    <TasksPaper elevation={0}>
      <TasksHeader>
        <Typography variant="h6" sx={{ 
          fontWeight: '700',
          fontSize: '1.1rem',
          color: '#000000',
          letterSpacing: '-0.01em',
        }}>
          Tareas
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Añadir tarea">
            <IconButton 
              size="small"
              component={Link}
              to="/tasks"
              sx={{ color: goldColor }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TasksHeader>
      
      <TasksContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} sx={{ color: goldColor }} />
          </Box>
        ) : tasks.length === 0 ? (
          <EmptyTasksBox>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              No hay tareas pendientes
            </Typography>
            <Button
              startIcon={<AddIcon />}
              component={Link}
              to="/tasks"
              variant="outlined"
              size="small"
              sx={{ 
                color: goldColor, 
                borderColor: goldColor,
                '&:hover': {
                  borderColor: '#c49b07',
                  backgroundColor: 'rgba(227, 174, 8, 0.08)',
                }
              }}
            >
              Añadir tarea
            </Button>
          </EmptyTasksBox>
        ) : (
          <List disablePadding>
            {tasks.map((task, index) => {
              const { formattedDate, isPast } = formatDueDate(task.dueDate);
              
              return (
                <React.Fragment key={task.taskId}>
                  <ListItem
                    sx={{
                      py: 1,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 38 }}>
                      <Checkbox
                        edge="start"
                        checked={task.completed}
                        onChange={() => handleToggleComplete(task.taskId, task.completed)}
                        sx={{ 
                          color: goldColor,
                          '&.Mui-checked': {
                            color: goldColor,
                          }
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            textDecoration: task.completed ? 'line-through' : 'none',
                            color: task.completed ? '#9e9e9e' : 'inherit',
                          }}
                        >
                          {task.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <PriorityChip
                            label={translatePriority(task.priority)}
                            size="small"
                            priority={task.priority}
                          />
                          {task.dueDate && (
                            <DueDateText isPast={isPast} variant="caption">
                              <EventIcon sx={{ fontSize: '0.9rem' }} />
                              {formattedDate}
                            </DueDateText>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{
                        component: 'div',
                      }}
                    />
                    <Tooltip title="Más opciones">
                      <IconButton 
                        edge="end" 
                        size="small"
                        component={Link}
                        to={`/tasks?id=${task.taskId}`}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  {index < tasks.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </TasksContent>
      
      <Box sx={{ 
        p: 1.5, 
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <ViewAllButton 
          component={Link} 
          to="/tasks"
          endIcon={<FlagIcon />}
        >
          Ver todas las tareas
        </ViewAllButton>
      </Box>
    </TasksPaper>
  );
};

export default TasksWidget;